import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  FaTrash,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaShareAlt,
} from "react-icons/fa";
import "./cart.css";
import { calculateShippingCharges } from "../utils/shippingCalculator.js";
import { setPageMeta } from "../utils/seo";

const ShoppingCart = ({ cartItems, onRemove, onQuantityChange, addToWishlist, onSaveForLater, clearCart }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showShareButtons, setShowShareButtons] = useState({});
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [removeId, setRemoveId] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    setPageMeta({
      title: "Shopping Cart | Banat India",
      description:
        "Manage your Banat India cart items and continue to a secure checkout.",
      keywords:
        "shopping cart, Banat India cart, oral care products, secure checkout",
    });
  }, []);
  const calculateSubtotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };
  const subtotal = calculateSubtotal();
  const subtotalAfterDiscount = subtotal - discount;
  
  // Calculate shipping charges
  const shippingInfo = calculateShippingCharges(subtotalAfterDiscount);
  const total = subtotalAfterDiscount + shippingInfo.shippingCharges;

  const handleCheckout = () => {
    if (!currentUser) {
      alert("Please sign in to proceed with checkout");
      navigate("/login");
      return;
    }
    navigate("/checkout");
  };

  const toggleShareButtons = (itemId) => {
    setShowShareButtons((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleShare = (platform, item) => {
    const text = `Check out ${item.name} for ₹${item.price} on our store!`;
    const url = window.location.href;
    switch (platform) {
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
          "_blank"
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}&quote=${encodeURIComponent(text)}`,
          "_blank"
        );
        break;
      case "instagram":
        navigator.clipboard
          .writeText(text + " " + url)
          .then(() => alert("Link copied! You can now share it on Instagram"))
          .catch(() => alert("Failed to copy link"));
        break;
      default:
        break;
    }
  };

  const handleRemove = (id) => {
    setRemoveId(id);
  };
  const confirmRemove = () => {
    if (onRemove && removeId) onRemove(removeId);
    setRemoveId(null);
  };
  const cancelRemove = () => setRemoveId(null);

  const handleClearCart = () => setShowClearDialog(true);
  const confirmClearCart = () => {
    if (clearCart) clearCart();
    setShowClearDialog(false);
  };
  const cancelClearCart = () => setShowClearDialog(false);

  const handleApplyCoupon = () => {
    // Dummy coupon logic for UI
    if (coupon === "BANAT10") {
      setDiscount(Math.floor(subtotal * 0.1));
      setAppliedCoupon("BANAT10");
    } else {
      setDiscount(0);
      setAppliedCoupon(null);
      alert("Invalid coupon code");
    }
  };

  return (
    <div className="cart-container">
      <h2>Shopping Cart</h2>
      {cartItems.length === 0 ? (
        <p>Your cart is empty</p>
      ) : (
        <div className="cart-main-flex">
          <div className="cart-items-col">
            {cartItems.map((item) => (
              <div key={item.id} className="cart-item">
                <a href={`/product/${item.id}`} tabIndex={0} aria-label={`View details for ${item.name}`}>
                  <img
                    src={item.image.default || item.image}
                    alt={item.name}
                    className="cart-item-image"
                  />
                </a>
                <div className="cart-item-details">
                  <a href={`/product/${item.id}`} tabIndex={0} aria-label={`View details for ${item.name}`}>
                    <h3>{item.name}</h3>
                  </a>
                  <p>₹{item.price} x {item.quantity} = <b>₹{item.price * item.quantity}</b></p>
                  <div className="quantity-controls">
                    <button
                      aria-label="Decrease quantity"
                      onClick={() => onQuantityChange(item.id, "decrease")}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      aria-label="Increase quantity"
                      onClick={() => onQuantityChange(item.id, "increase")}
                    >
                      +
                    </button>
                  </div>
                  <div className="cart-item-action-row">
                    <button
                      className="action-button save-for-later"
                      onClick={() => onSaveForLater && onSaveForLater(item)}
                      aria-label="Save for later"
                    >
                      Save for Later
                    </button>
                    {addToWishlist && (
                      <button
                        className="action-button wishlist"
                        onClick={() => addToWishlist(item)}
                        aria-label="Move to wishlist"
                      >
                        Move to Wishlist
                      </button>
                    )}
                  </div>
                </div>
                <div className="cart-item-actions">
                  <button
                    className="action-button delete"
                    onClick={() => handleRemove(item.id)}
                    aria-label="Remove from cart"
                  >
                    <FaTrash />
                  </button>
                  <div className="share-buttons-container">
                    <button
                      className="action-button share"
                      onClick={() => toggleShareButtons(item.id)}
                      aria-label="Share product"
                    >
                      {showShareButtons[item.id] ? (
                        <div className="social-icons">
                          <button
                            className="social-share-icon whatsapp"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare("whatsapp", item);
                            }}
                            aria-label="Share on WhatsApp"
                          >
                            <FaWhatsapp />
                          </button>
                          <button
                            className="social-share-icon facebook"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare("facebook", item);
                            }}
                            aria-label="Share on Facebook"
                          >
                            <FaFacebook />
                          </button>
                          <button
                            className="social-share-icon instagram"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShare("instagram", item);
                            }}
                            aria-label="Share on Instagram"
                          >
                            <FaInstagram />
                          </button>
                        </div>
                      ) : (
                        <FaShareAlt />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="cart-summary-col">
            <div className="cart-summary">
              <div className="cart-summary-row">
                <span>Subtotal:</span>
                <span>₹{subtotal}</span>
              </div>
              {discount > 0 && (
                <div className="cart-summary-row">
                  <span>Discount ({appliedCoupon}):</span>
                  <span>-₹{discount}</span>
                </div>
              )}
              <div className="cart-shipping-bar">
                <span>Shipping:</span>
                <span className={`cart-shipping ${shippingInfo.isFreeShipping ? 'free-shipping' : ''}`}>
                  {shippingInfo.isFreeShipping ? 'Free' : `₹${shippingInfo.shippingCharges}`}
                </span>
              </div>
              <div className="cart-summary-row grand-total">
                <span>Total:</span>
                <span>₹{total}</span>
              </div>
              <div className="coupon-row">
                <input
                  type="text"
                  placeholder="Coupon code"
                  value={coupon}
                  onChange={e => setCoupon(e.target.value)}
                  aria-label="Coupon code"
                />
                <button onClick={handleApplyCoupon} aria-label="Apply coupon">Apply</button>
              </div>
              <div className="shipping-info-note">
                <div className="shipping-header">
                  <h4>Shipping Charges</h4>
                </div>
                <div className="shipping-tiers">
                  <div className="shipping-tier">
                    <div className="tier-content">
                      <span className="tier-range">Below ₹500</span>
                      <span className="tier-price">₹100</span>
                    </div>
                  </div>
                  <div className="shipping-tier">
                    <div className="tier-content">
                      <span className="tier-range">₹500 - ₹999</span>
                      <span className="tier-price">₹75</span>
                    </div>
                  </div>
                  <div className="shipping-tier free-tier">
                    <div className="tier-content">
                      <span className="tier-range">₹1000 & above</span>
                      <span className="tier-price">Free Delivery</span>
                    </div>
                  </div>
                </div>
                <div className="shipping-tip">
                  <strong>Pro Tip:</strong> Add ₹{Math.max(0, 1000 - subtotalAfterDiscount)} more to get FREE delivery!
                </div>
              </div>
              <button
                className="checkout-button"
                onClick={handleCheckout}
                disabled={cartItems.length === 0}
                aria-label="Proceed to checkout"
              >
                Proceed to Checkout
              </button>
              <button
                className="clear-cart-button"
                onClick={handleClearCart}
                aria-label="Clear cart"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Remove confirmation dialog */}
      {removeId && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <p>Are you sure you want to remove this item from your cart?</p>
            <button onClick={confirmRemove}>Yes, remove</button>
            <button onClick={cancelRemove}>Cancel</button>
          </div>
        </div>
      )}
      {/* Clear cart confirmation dialog */}
      {showClearDialog && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <p>Are you sure you want to clear your cart?</p>
            <button onClick={confirmClearCart}>Yes, clear</button>
            <button onClick={cancelClearCart}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingCart;
